import type { AdvisorData } from '../../utils/types'
import { deriveMatchTypes } from './deriveMatchTypes'
import { resolveAssetTier } from './resolveAssetTier'
import { deriveFunnelAnswers } from './deriveFunnelAnswers'
import type { PersonalizationContext } from './applyPersonalization'

export interface RawPersonalizationInputs {
  matches: AdvisorData[] | undefined
  matchedSavings: string | undefined
  funnelAnswers: unknown
  selfSchedulingEnabled: boolean
  revisit: boolean
}

/**
 * The single, shared derivation of `PersonalizationContext`, used by both evaluation sites:
 * `usePersonalizedModuleConfig` (render) and `registerPersonalizationCopy` (bootstrap
 * analytics). Sharing it keeps the derivation from diverging, though each site still reads its
 * own raw inputs.
 *
 * `selfScheduleEligible` is gated on `!revisit`: a revisit session hides the self-schedule
 * booking CTA, so its copy must not promise one. `answers` is deliberately NOT revisit-gated --
 * a session's answers are stable, so the same answers-matched copy renders on revisit too. By
 * design, that makes a rule combining `answers` with `selfSchedule: true` unreachable on
 * revisit.
 */
export function buildPersonalizationContext (raw: RawPersonalizationInputs): PersonalizationContext {
  return {
    matchTypes: deriveMatchTypes(raw.matches),
    tier: resolveAssetTier(raw.matchedSavings),
    selfScheduleEligible: raw.selfSchedulingEnabled && !raw.revisit,
    answers: deriveFunnelAnswers(raw.funnelAnswers)
  }
}
